/**
 * InDeckFilter - Filter toggle for deck membership
 *
 * Provides mutually exclusive "In Deck" / "Not in Deck" toggles
 * with an optional deck dropdown to filter by specific deck.
 */
import type { ReactElement } from 'react';

import Dropdown from '@/components/Dropdown/Dropdown';
import FilterChip from '@/components/FilterChip/FilterChip';
import type { Deck } from '@/types/deck';

export type InDeckFilterValue = 'in-deck' | 'not-in-deck';

export interface InDeckFilterProps {
  /** Current filter value */
  value: InDeckFilterValue | undefined;
  /** Selected deck ID (undefined = any deck) */
  deckId: string | undefined;
  /** Called when the filter toggle changes */
  onValueChange: (value: InDeckFilterValue | undefined) => void;
  /** Called when the deck selection changes */
  onDeckChange: (deckId: string | undefined) => void;
  /** Available decks to filter by */
  decks: Deck[];
}

export default function InDeckFilter({ value, deckId, onValueChange, onDeckChange, decks }: InDeckFilterProps): ReactElement | null {
  // Don't render if there are no decks
  if (decks.length === 0) {
    return null;
  }

  const handleInDeckClick = () => {
    if (value === 'in-deck') {
      // Deselect and clear deck
      onValueChange(undefined);
      onDeckChange(undefined);
    } else {
      // Select "in deck", clear any "not in deck"
      onValueChange('in-deck');
    }
  };

  const handleNotInDeckClick = () => {
    if (value === 'not-in-deck') {
      // Deselect and clear deck
      onValueChange(undefined);
      onDeckChange(undefined);
    } else {
      // Select "not in deck", clear any "in deck"
      onValueChange('not-in-deck');
    }
  };

  const handleDeckChange = (selectedDeckId: string | null) => {
    onDeckChange(selectedDeckId ?? undefined);
  };

  // Build dropdown options from decks
  const deckOptions = decks.map((deck) => ({
    value: deck.id,
    label: deck.name,
  }));

  const isActive = value !== undefined;

  return (
    <div className="flex items-center gap-2">
      <FilterChip label="In Deck" isActive={value === 'in-deck'} onClick={handleInDeckClick} />
      <FilterChip label="Not in Deck" isActive={value === 'not-in-deck'} onClick={handleNotInDeckClick} />

      {isActive && (
        <Dropdown
          value={deckId ?? null}
          options={deckOptions}
          onChange={handleDeckChange}
          placeholder="Any deck"
          allowEmpty
          size="sm"
          aria-label="Select deck to filter by"
        />
      )}
    </div>
  );
}
